// auditLog.js — shared helper for `marketing_{campaigns|segments|automations}/{id}/auditLog`.
// Provides:
//   writeAuditEntry(collection, docId, entry)  — Firestore write helper
//   computeDiff(before, after, ignoreKeys)     — shallow field diff for `changes` field
//   <AuditLogCard collection={} docId={} />    — sidebar display component
//
// Schema per MARKETING_CAMPAIGNS_PLAN §3.6:
//   { at: Timestamp, by, byName, action, changes?, notes? }
//
// Actions: created | edited | scheduled | launched | canceled | completed
//        | enabled | disabled | active | inactive | deleted | test-fired

import React, { useEffect, useState } from 'react';
import { Button, Card, CardBody, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import PerfectScrollbar from 'react-perfect-scrollbar';
import moment from 'moment';
import { firestore, functions } from '../../../../helpers/Firebase';

// Fields we never treat as an "edit" — either they're managed by the server
// (stats, updatedAt) or they're identity fields that only change on create.
const DEFAULT_IGNORE_KEYS = new Set([
  'updatedAt', 'updatedBy', 'updatedByName',
  'createdAt', 'createdBy', 'createdByName',
  'stats', 'statsReconsolidatedAt',
  'segmentSnapshot',           // frozen at schedule time, not user-edited
  'audience.resolved', 'audience.noToken', 'audience.optedOut', 'audience.suppressed',
]);

// Return { key: { from, to } } for every top-level key whose stringified value
// differs. Deep diff isn't worth the maintenance cost given the audit is human-facing.
export const computeDiff = (before = {}, after = {}, ignore = []) => {
  const ignoreSet = new Set([...DEFAULT_IGNORE_KEYS, ...ignore]);
  const changes = {};
  const allKeys = new Set([...Object.keys(before || {}), ...Object.keys(after || {})]);
  for (const k of allKeys) {
    if (ignoreSet.has(k)) continue;
    const b = before ? before[k] : undefined;
    const a = after ? after[k] : undefined;
    if (JSON.stringify(b) !== JSON.stringify(a)) {
      changes[k] = { from: b, to: a };
    }
  }
  return changes;
};

// Write a single audit entry via the `writeMarketingAudit` Cloud callable.
// Firestore rules block client-side writes to auditLog subcollections
// intentionally — the Admin SDK on the server is the sole write path so
// entries are immutable and actor-authentic. Fire-and-forget: bookkeeping
// failures must never block the primary save flow.
//
// The server ignores whatever `by`/`byName` we pass and uses context.auth.uid.
// We still accept them here for API symmetry with the client-side actor helper.
export const writeAuditEntry = async (collection, docId, entry) => {
  if (!collection || !docId || !entry?.action) return;
  try {
    const call = functions().httpsCallable('writeMarketingAudit');
    await call({
      collection,
      docId,
      action: entry.action,
      ...(entry.changes && Object.keys(entry.changes).length > 0 ? { changes: entry.changes } : {}),
      ...(entry.notes ? { notes: entry.notes } : {}),
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('auditLog write failed', collection, docId, entry.action, e?.message || e);
  }
};

// Small helper to build the (by, byName) pair from a redux user object.
export const actorFromUser = (user) => ({
  by: user?.uid || null,
  byName: user?.displayName || user?.email || null,
});

const ACTION_LABELS = {
  created:    { label: 'Created',      color: '#28a745' },
  edited:     { label: 'Edited',       color: '#0d6efd' },
  scheduled:  { label: 'Scheduled',    color: '#0d6efd' },
  launched:   { label: 'Launched',     color: '#28a745' },
  canceled:   { label: 'Canceled',     color: '#dc3545' },
  completed:  { label: 'Completed',    color: '#6c757d' },
  enabled:    { label: 'Enabled',      color: '#28a745' },
  disabled:   { label: 'Disabled',     color: '#6c757d' },
  active:     { label: 'Activated',    color: '#28a745' },
  inactive:   { label: 'Deactivated',  color: '#6c757d' },
  deleted:    { label: 'Deleted',      color: '#dc3545' },
  'test-fired': { label: 'Test-fired', color: '#f0ad4e' },
};

// Sidebar card that streams the parent doc's auditLog subcollection.
// Only shows once the parent has been saved (docId present).
export const AuditLogCard = ({ collection, docId, limit = 20 }) => {
  const [entries, setEntries] = useState(null);

  useEffect(() => {
    if (!collection || !docId) return;
    const unsub = firestore()
      .collection(collection)
      .doc(docId)
      .collection('auditLog')
      .orderBy('at', 'desc')
      .limit(limit)
      .onSnapshot(
        (snap) => setEntries(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
        (e) => console.error('auditLog listener', e),
      );
    return () => unsub();
  }, [collection, docId, limit]);

  if (!collection || !docId) return null;

  return (
    <Card>
      <CardBody>
        <h6 className="mb-2">History</h6>
        {entries === null && (
          <div className="small text-muted">Loading…</div>
        )}
        {entries !== null && entries.length === 0 && (
          <div className="small text-muted">No history yet.</div>
        )}
        {entries !== null && entries.length > 0 && (
          <div className="small" style={{ maxHeight: 320, overflowY: 'auto' }}>
            {entries.map((e) => {
              const meta = ACTION_LABELS[e.action] || { label: e.action, color: '#6c757d' };
              const changeCount = e.changes ? Object.keys(e.changes).length : 0;
              return (
                <div key={e.id} className="mb-2 pb-2 border-bottom">
                  <div className="d-flex justify-content-between align-items-baseline">
                    <span style={{ color: meta.color, fontWeight: 600 }}>{meta.label}</span>
                    <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                      {e.at ? moment(e.at).fromNow() : ''}
                    </span>
                  </div>
                  <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                    by {[e.byName, e.byRole].filter(Boolean).join(' ') || e.by || 'system'}
                    {changeCount > 0 && (
                      <> · {changeCount} field{changeCount === 1 ? '' : 's'}: <em>{Object.keys(e.changes).slice(0, 4).join(', ')}{changeCount > 4 ? '…' : ''}</em></>
                    )}
                  </div>
                  {e.notes && (
                    <div className="text-muted mt-1" style={{ fontSize: '0.75rem' }}>{e.notes}</div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardBody>
    </Card>
  );
};

// Modal variant of AuditLogCard — same subscription + row rendering, wrapped
// in a Reactstrap Modal so lists can pop it from a row action button instead
// of forcing users into the editor. Same visual language as the orders/
// appointments Logs modal (color-tagged action + author + timestamp).
export const AuditLogModal = ({ isOpen, collection, docId, title, dismiss }) => {
  const [entries, setEntries] = useState(null);

  useEffect(() => {
    if (!isOpen || !collection || !docId) { setEntries(null); return; }
    const unsub = firestore()
      .collection(collection)
      .doc(docId)
      .collection('auditLog')
      .orderBy('at', 'desc')
      .limit(200)
      .onSnapshot(
        (snap) => setEntries(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
        (e) => console.error('auditLog modal listener', e),
      );
    return () => unsub();
  }, [isOpen, collection, docId]);

  return (
    <Modal
      isOpen={!!isOpen}
      toggle={dismiss}
      backdrop="static"
      size="lg"
      style={{ boxShadow: 'none', maxWidth: '720px', top: '30px', borderRadius: '14px' }}
    >
      <ModalHeader toggle={dismiss}>
        History{title ? ` — ${title}` : ''}
      </ModalHeader>
      <ModalBody>
        {entries === null && (
          <div className="text-muted small p-2">Loading…</div>
        )}
        {entries !== null && entries.length === 0 && (
          <div className="text-muted p-2" style={{ textAlign: 'center' }}>
            No history yet.
          </div>
        )}
        {entries !== null && entries.length > 0 && (
          <PerfectScrollbar
            option={{ suppressScrollX: true, wheelPropagation: false }}
            style={{ maxHeight: 480 }}
          >
            <table className="table table-sm table-borderless mb-0">
              <tbody>
                {entries.map((e) => {
                  const meta = ACTION_LABELS[e.action] || { label: e.action, color: '#6c757d' };
                  const changeCount = e.changes ? Object.keys(e.changes).length : 0;
                  const actor = [e.byName, e.byRole].filter(Boolean).join(' ') || e.by || 'system';
                  return (
                    <tr key={e.id}>
                      <td style={{ width: 8, padding: '10px 4px 10px 8px', verticalAlign: 'top' }}>
                        <span
                          style={{
                            display: 'inline-block',
                            width: 8, height: 8, borderRadius: '50%',
                            marginTop: 6, background: meta.color,
                          }}
                        />
                      </td>
                      <td style={{ padding: '10px 8px' }}>
                        <div>
                          <span style={{ color: meta.color, fontWeight: 600 }}>{meta.label}</span>
                          <span className="text-muted"> by {actor}</span>
                        </div>
                        {changeCount > 0 && (
                          <div className="small text-muted mt-1">
                            {changeCount} field{changeCount === 1 ? '' : 's'} changed:{' '}
                            <em>{Object.keys(e.changes).slice(0, 6).join(', ')}{changeCount > 6 ? '…' : ''}</em>
                          </div>
                        )}
                        {e.notes && (
                          <div className="small text-muted mt-1">{e.notes}</div>
                        )}
                      </td>
                      <td className="text-right text-muted" style={{ width: 170, padding: '10px 8px', verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                        {e.at ? moment(e.at).format('DD/MM/YYYY hh:mm A') : ''}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </PerfectScrollbar>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" outline onClick={dismiss}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

// Small hook that manages the { isOpen, row } state for a list-page audit
// modal. Returns `{ open(row), close, isOpen, row }` — the callable form
// keeps every list's wiring tiny (one useState hook + one action button).
export const useAuditLogModal = () => {
  const [state, setState] = useState({ isOpen: false, row: null });
  return {
    isOpen: state.isOpen,
    row: state.row,
    open: (row) => setState({ isOpen: true, row }),
    close: () => setState({ isOpen: false, row: null }),
  };
};

